import React, { useCallback, useEffect, useState } from "react";
import { Plus, Trash2, ArrowUpRight, ArrowDownLeft, Check, X, AlertTriangle } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { RagBadge, RAG_OPTIONS } from "@/components/Rag";
import { StatusPill } from "@/components/LogSection";
import { Select } from "@/pages/Projects";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

const ESCALATION = ["None", "Level 1", "Level 2", "Level 3"];
const BLANK = {
  title: "", description: "", direction: "Give", counterparty_project_id: "", owner: "",
  counterparty_owner: "", due_date: "", rag: "Green", escalation_level: "None", notes: "",
};

const HEADERS = ["Dependency", "Counterparty Project", "Owner", "Due", "RAG", "Status", "Escalation", ""];

const DepTable = ({ rows, kind, icon: Icon, blurb, heading, projectName, onRespond, onRemove, projectId }) => (
  <section className="panel p-5" data-testid={`dependencies-${kind}-section`}>
    <div className="mb-1 flex items-center gap-2">
      <Icon size={16} style={{ color: "var(--brand-primary)" }} />
      <h3 className="font-display text-base font-semibold text-slate-800">{heading}</h3>
    </div>
    <p className="mb-3 text-xs text-slate-500">{blurb}</p>
    {rows.length === 0 ? (
      <p className="py-6 text-center text-sm text-slate-400">No {kind} dependencies.</p>
    ) : (
      <div className="-mx-5 overflow-x-auto px-5">
        <table className="w-full min-w-[880px] text-sm">
          <thead className="text-left text-[11px] uppercase tracking-wider text-slate-500">
            <tr>{HEADERS.map((h) => <th key={h} className="pb-2 pr-3 font-semibold">{h}</th>)}</tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map((d) => (
              <tr key={d.id} data-testid={`dependency-row-${d.id}`}>
                <td className="py-2.5 pr-3">
                  <div className="font-medium text-slate-800">{d.title}</div>
                  {d.response_note && <div className="text-xs text-slate-400">Response: {d.response_note}</div>}
                  {d.status === "Proposed" && d.raised_by_project_id === projectId && (
                    <div className="text-xs text-slate-400">Raised here — awaiting counterparty response</div>
                  )}
                </td>
                <td className="py-2.5 pr-3 text-slate-600">{projectName(d.counterparty_project_id)}</td>
                <td className="py-2.5 pr-3 text-slate-600">{d.owner || "—"}</td>
                <td className="py-2.5 pr-3 text-slate-600">{d.due_date || "—"}</td>
                <td className="py-2.5 pr-3"><RagBadge value={d.rag} /></td>
                <td className="py-2.5 pr-3"><StatusPill value={d.status} /></td>
                <td className="py-2.5 pr-3 text-slate-600">
                  {d.escalation_level && d.escalation_level !== "None" ? (
                    <span className="inline-flex items-center gap-1 text-amber-700"><AlertTriangle size={13} /> {d.escalation_level}</span>
                  ) : "—"}
                </td>
                <td className="py-2.5 text-right">
                  <div className="flex items-center justify-end gap-2">
                    {d.status === "Proposed" && d.raised_by_project_id !== projectId && (
                      <Button size="sm" variant="outline" data-testid={`respond-dependency-${d.id}`} onClick={() => onRespond(d)} className="h-7 px-2 text-xs">
                        Respond
                      </Button>
                    )}
                    <button data-testid={`delete-dependency-${d.id}`} onClick={() => onRemove(d)} aria-label="Delete dependency" title="Delete dependency" className="text-slate-400 transition-colors hover:text-red-600">
                      <Trash2 size={15} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </section>
);

export const Dependencies = ({ projectId }) => {
  const [deps, setDeps] = useState([]);
  const [projects, setProjects] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(BLANK);
  const [responding, setResponding] = useState(null);
  const [note, setNote] = useState("");

  const load = useCallback(
    () => api.get(`/projects/${projectId}/dependencies`).then(({ data }) => setDeps(data)),
    [projectId],
  );

  useEffect(() => {
    load();
    api.get("/projects").then(({ data }) => setProjects(data.filter((p) => p.id !== projectId)));
  }, [load, projectId]);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.title.trim()) return toast.error("Title is required");
    if (!form.counterparty_project_id) return toast.error("Select the counterparty project");
    try {
      await api.post(`/projects/${projectId}/dependencies`, form);
      toast.success("Dependency raised — awaiting the counterparty's acceptance");
      setOpen(false);
      setForm(BLANK);
      await load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const respond = async (action) => {
    try {
      await api.post(`/dependencies/${responding.id}/respond`, { action, note: note || null, project_id: projectId });
      toast.success(`Dependency ${action.toLowerCase()}`);
      setResponding(null);
      setNote("");
      await load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const remove = async (d) => {
    if (!window.confirm(`Delete dependency "${d.title}"?`)) return;
    try {
      await api.delete(`/dependencies/${d.id}`);
      await load();
      toast.success("Deleted");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const projectName = (id) => projects.find((p) => p.id === id)?.name || "—";

  return (
    <div className="space-y-6" data-testid="dependencies-tab">
      <div className="flex justify-end">
        <Button data-testid="add-dependency-btn" onClick={() => { setForm(BLANK); setOpen(true); }} className="gap-1.5" style={{ backgroundColor: "var(--brand-primary)" }}>
          <Plus size={14} /> Raise Dependency
        </Button>
      </div>

      <DepTable
        rows={deps.filter((d) => d.direction === "Give")}
        kind="give"
        icon={ArrowUpRight}
        heading="Give — we owe another project"
        blurb="Commitments this project must deliver to another project."
        projectName={projectName}
        projectId={projectId}
        onRespond={(d) => { setResponding(d); setNote(""); }}
        onRemove={remove}
      />
      <DepTable
        rows={deps.filter((d) => d.direction === "Get")}
        kind="get"
        icon={ArrowDownLeft}
        heading="Get — another project owes us"
        blurb="Commitments this project is relying on from another project."
        projectName={projectName}
        projectId={projectId}
        onRespond={(d) => { setResponding(d); setNote(""); }}
        onRemove={remove}
      />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="dependency-dialog" className="max-h-[88vh] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Raise Dependency</DialogTitle>
            <DialogDescription>Give/Get handshake — the counterparty project must accept before it is live.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="sm:col-span-2"><label className="field-label">Title *</label><Input data-testid="dependency-title-input" value={form.title} onChange={(e) => set("title")(e.target.value)} /></div>
            <div><label className="field-label">Direction</label><Select testId="dependency-direction-select" value={form.direction} onChange={set("direction")} options={["Give", "Get"]} placeholder="Select" /></div>
            <div>
              <label className="field-label">Counterparty Project *</label>
              <Select testId="dependency-counterparty-select" value={form.counterparty_project_id} onChange={set("counterparty_project_id")} options={projects.map((p) => ({ value: p.id, label: p.name }))} placeholder="Select project" />
            </div>
            <div><label className="field-label">Our Owner</label><Input data-testid="dependency-owner-input" value={form.owner} onChange={(e) => set("owner")(e.target.value)} /></div>
            <div><label className="field-label">Counterparty Owner</label><Input data-testid="dependency-cowner-input" value={form.counterparty_owner} onChange={(e) => set("counterparty_owner")(e.target.value)} /></div>
            <div><label className="field-label">Due Date</label><Input data-testid="dependency-due-input" type="date" value={form.due_date} onChange={(e) => set("due_date")(e.target.value)} /></div>
            <div><label className="field-label">RAG</label><Select testId="dependency-rag-select" value={form.rag} onChange={set("rag")} options={RAG_OPTIONS} placeholder="Select" /></div>
            <div><label className="field-label">Escalation Level</label><Select testId="dependency-escalation-select" value={form.escalation_level} onChange={set("escalation_level")} options={ESCALATION} placeholder="Select" /></div>
            <div className="sm:col-span-2"><label className="field-label">Description</label><Textarea data-testid="dependency-description-input" rows={2} value={form.description} onChange={(e) => set("description")(e.target.value)} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" data-testid="dependency-cancel-btn" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="dependency-save-btn" onClick={save} style={{ backgroundColor: "var(--brand-primary)" }}>Raise Dependency</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!responding} onOpenChange={(o) => !o && setResponding(null)}>
        <DialogContent data-testid="dependency-respond-dialog">
          <DialogHeader>
            <DialogTitle>Respond to Dependency</DialogTitle>
            <DialogDescription>{responding?.title}</DialogDescription>
          </DialogHeader>
          <div>
            <label className="field-label">Response Note</label>
            <Textarea data-testid="dependency-response-note" rows={3} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Optional context for the other project team" />
          </div>
          <DialogFooter>
            <Button variant="outline" data-testid="dependency-reject-btn" onClick={() => respond("Rejected")} className="gap-1.5 text-red-700"><X size={14} /> Reject</Button>
            <Button data-testid="dependency-accept-btn" onClick={() => respond("Accepted")} className="gap-1.5" style={{ backgroundColor: "var(--brand-primary)" }}><Check size={14} /> Accept</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
